import type { QueryClient } from "@tanstack/react-query";
import { useCatalogRebuild } from "../library/catalogRebuildStore.js";
import { quietFor } from "../library/perfLog.js";
import { healthProbesInFlight } from "../library/runHealthProbe.js";
import { channelChecksInFlight } from "../library/channelCheck.js";
import { usePlayerStore } from "../player/playerStore.js";

/**
 * What the page is busy with right now, for the debug server's `/state` and
 * `dbg idle`.
 *
 * WHY (projector, 2026-09-24). A timing run of Back from the guide to Home was
 * made while the daily catalog rebuild was running, and the two fought over a
 * 1 GB device's processor: the rebuild took longer, one measurement got no
 * answer for 30 s, and nothing on either side said why. Every measurement is
 * only as good as knowing the page was otherwise idle; this says whether it was.
 *
 * Names only — query-key heads ("epg", "channels"), never a key's arguments,
 * which can carry a provider id or a search.
 */
export interface PageBusy {
  /** The APK rebuilding its stored catalog ("Optimizing your app"). */
  catalogRebuild: { on: boolean; forMs: number | null };
  /** Network queries in flight, by the first part of their key. */
  fetching: { count: number; kinds: string[] };
  /** Channel-health requests out. */
  healthProbes: number;
  /** The player's own state: "loading" and "buffering" are busy. */
  player: string;
  /** ms since the last long task / slow frame ended (null: none yet). */
  quiet: { longTaskMs: number | null; slowFrameMs: number | null };
}

export function pageBusy(queryClient: QueryClient | null): PageBusy {
  const rebuild = useCatalogRebuild.getState();
  const fetchingQueries = queryClient ? queryClient.getQueryCache().findAll({ fetchStatus: "fetching" }) : [];
  const kinds = [...new Set(fetchingQueries.map((query) => String(query.queryKey[0] ?? "?")))];
  return {
    catalogRebuild: { on: rebuild.rebuilding, forMs: rebuild.startedAt ? Date.now() - rebuild.startedAt : null },
    fetching: { count: fetchingQueries.length, kinds },
    healthProbes: healthProbesInFlight() + channelChecksInFlight(),
    player: usePlayerStore.getState().status,
    quiet: quietFor(),
  };
}
